import { FileDto } from '@act/shared/data-transfer-objects';
import {
  createAction,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { filesRequests } from '../requests/files.requests';
import { LoadingStatus } from '../types/sharedTypes';
import { createApiThunk } from '../utils/apiThunk';
import { resetRedux } from '../actions/sharedActions';

// THUNKS/ACTIONS ==============================================================
export type LoadFilePayload = { id: string };
export const loadFile = createAction<LoadFilePayload>('files/loadFile');

// INITIAL STATE ===============================================================
export interface FileState {
  id: string;
  loadingStatus: LoadingStatus;
  file?: FileDto;
}
export const getFile = createApiThunk<FileDto, null, { id: string }>(
  filesRequests.getFileInfo
);

const fileInfoAdapter = createEntityAdapter<FileState>();
const initialState = fileInfoAdapter.getInitialState();

// SLICE =======================================================================
export const filesSlice = createSlice({
  name: 'files',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getFile.pending, (state, action) => {
        const fileDetail: FileState = {
          id: action.meta.arg.params.id,
          loadingStatus: 'loading',
        };
        fileInfoAdapter.upsertOne(state, fileDetail);
      })
      .addCase(getFile.fulfilled, (state, action) => {
        const fileDetail: FileState = {
          id: action.payload.id,
          loadingStatus: 'success',
          file: action.payload,
        };
        fileInfoAdapter.upsertOne(state, fileDetail);
      })
      .addCase(getFile.rejected, (state, action) => {
        const fileDetail: FileState = {
          id: action.meta.arg.params.id,
          loadingStatus: 'failed',
        };
        fileInfoAdapter.upsertOne(state, fileDetail);
      });
  },
});

// REDUCER =====================================================================
export const filesReducer = filesSlice.reducer;

// SELECTORS ===================================================================
const selectFilesState = state =>
  (state as { files: typeof initialState })?.files;

export const { selectEntities: selectFilesMap } =
  fileInfoAdapter.getSelectors(selectFilesState);
// EXPORT HELPER ===============================================================

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadFile,
    effect: async ({ dispatch, state, action }) => {
      const files = selectFilesState(state);
      if (!files[action.payload.id]) {
        dispatch(getFile({ params: { id: action.payload.id } }));
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
